import React from 'react';
import {Button, Card, ListItem, ListItemText, Stack} from '@mui/material';
import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {HighlightedText} from 'presentation/components';
import {useIsAdvanceDiagnosticsExamsJourney} from 'presentation/modules/booking/exam/hooks/useIsAdvanceDiagnosticsExamsJourney';
import getFormattedHour from 'presentation/pages/private/booking/utils/getFormattedHour';
import {formatText} from 'presentation/utils';
import {StyledStack} from './styles';
import {ProfessionalCardProps} from './types';
import {ExpandableHourList} from './components/ExpandableHourList';
import {FavoriteDoctorsLikeButton} from 'presentation/modules/booking/favoriteDoctors/components/FavoriteDoctorsLikeButton';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';

export const ProfessionalCard = React.memo(function ProfessionalCard({
	professionalCode,
	professionalName,
	availableTimes,
	date,
	onSelectHour,
	recommendations,
	flowType,
	query,
	shouldNotUseHighlight,
	isFavorite,
}: ProfessionalCardProps) {
	const {specialtyCode, subSpecialtyCode} = useBookingStore(state => state.bookingData);
	const isAdvanceDiagnosticsExam =
		useIsAdvanceDiagnosticsExamsJourney() && flowType === FlowEnum.EXAM;

	const handleSelectHour = (hour: string) =>
		onSelectHour({
			hour,
			profCode: professionalCode,
			profName: professionalName,
			date,
			recommendations,
		});

	return (
		<ListItem disableGutters>
			<Card
				elevation={0}
				sx={{p: 2, width: '100%'}}
				data-tag-id={AppointmentBookingEnum.VISUALIZOU_PROFISSIONAL_DISPONIVEL}>
				<Stack flexDirection="row" alignItems="center" justifyContent="space-between">
					<ListItemText
						primary={
							shouldNotUseHighlight ? (
								formatText(professionalName)
							) : (
								<HighlightedText
									highlight={query}
									text={formatText(professionalName)}
								/>
							)
						}
						slotProps={{
							primary: {
								variant: isAdvanceDiagnosticsExam ? 'body2' : 'h6',
							},
						}}
					/>
					{flowType === FlowEnum.HEALTH || flowType === FlowEnum.TELEHEALTH ? (
						<FavoriteDoctorsLikeButton
							cdPrestador={Number(professionalCode)}
							cdEspecialidade={Number(specialtyCode)}
							cdSubEspecialidade={Number(subSpecialtyCode)}
							selected={isFavorite}
						/>
					) : null}
				</Stack>

				{isAdvanceDiagnosticsExam ? (
					<Stack mt={2}>
						<ExpandableHourList
							availableTimes={availableTimes}
							flowType={flowType}
							onSelectHour={handleSelectHour}
						/>
					</Stack>
				) : (
					<StyledStack direction="row" gap={1} overflow="auto" mt={2} pb={2}>
						{availableTimes.map((hour, index) => {
							return (
								<Button
									key={`${hour}-${index}`}
									data-test="button-hour"
									variant={'outlined'}
									sx={{borderRadius: 0.5, fontSize: '1rem'}}
									data-tag-id={
										AppointmentBookingEnum.SELECIONOU_HORARIO_DISPONIVEL
									}
									onClick={() => handleSelectHour(hour)}>
									{getFormattedHour(hour, flowType)}
								</Button>
							);
						})}
					</StyledStack>
				)}
			</Card>
		</ListItem>
	);
});
